<template>
  <div class="admin-resign-container">

    <!-- ── TOP BAR ─────────────────────────────────────────────────── -->
    <v-app-bar flat color="white" elevation="1" class="px-4">
      <v-icon color="error" class="mr-2">mdi-account-off</v-icon>
      <v-toolbar-title class="font-weight-bold error--text">
        รายงานพนักงานราชการพ้นสภาพ (Admin)
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip outlined color="grey" small class="mr-3">
        <v-icon left x-small>mdi-database</v-icon>
        ทั้งหมด {{ totalCount.toLocaleString() }} ราย
      </v-chip>
      <v-btn color="success" depressed rounded small @click="exportExcel" :loading="exporting">
        <v-icon left small>mdi-microsoft-excel</v-icon> Export Excel
      </v-btn>
    </v-app-bar>

    <v-container fluid class="pa-4 pa-md-6">

      <!-- ── SUMMARY CARDS ──────────────────────────────────────────── -->
      <v-row class="mb-5">
        <v-col cols="6" sm="4" md="2" v-for="card in summaryCards" :key="card.label">
          <v-card class="rounded-xl text-center pa-3" elevation="2" :class="card.bg">
            <v-icon :color="card.iconColor" size="28">{{ card.icon }}</v-icon>
            <div class="text-h5 font-weight-black mt-1" :class="card.textColor">
              {{ card.value }}
            </div>
            <div class="text-caption grey--text">{{ card.label }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── FILTER PANEL ────────────────────────────────────────────── -->
      <v-card class="rounded-xl mb-5" elevation="1">
        <v-card-title class="text-body-1 font-weight-bold py-3 px-5 grey lighten-5">
          <v-icon left small color="primary">mdi-filter-variant</v-icon>
          ตัวกรองข้อมูล
          <v-spacer></v-spacer>
          <v-btn text x-small color="grey" @click="clearFilters">
            <v-icon x-small left>mdi-refresh</v-icon> ล้างตัวกรอง
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row dense>
            <!-- ค้นหา -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="filters.search"
                label="ค้นหา ชื่อ / เลขบัตร / ตำแหน่ง"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-magnify"
                @input="onFilterChange"
              ></v-text-field>
            </v-col>

            <!-- วิทยาลัย -->
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="filters.college_code"
                :items="collegeOptions"
                item-text="college_name"
                item-value="college_code"
                label="วิทยาลัย"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-domain"
                @change="onFilterChange"
              ></v-autocomplete>
            </v-col>

            <!-- ภาค -->
            <v-col cols="12" md="2">
              <v-select
                v-model="filters.region_name"
                :items="regionOptions"
                item-text="region_name"
                item-value="region_name"
                label="ภาค"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-map-marker-radius"
                @change="onFilterChange"
              ></v-select>
            </v-col>

            <!-- ประเภทการพ้นสภาพ -->
            <v-col cols="12" md="2">
              <v-select
                v-model="filters.reason_type"
                :items="reasonTypeOptions"
                label="ประเภทการพ้นสภาพ"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-format-list-bulleted-type"
                @change="onFilterChange"
              ></v-select>
            </v-col>

            <!-- ประเภทตำแหน่ง -->
            <v-col cols="12" md="2">
              <v-select
                v-model="filters.type_position"
                :items="['ตำแหน่งในภารกิจหลัก','ตำแหน่งในภารกิจสนับสนุน']"
                label="ประเภทตำแหน่ง"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-briefcase-variant"
                @change="onFilterChange"
              ></v-select>
            </v-col>

            <!-- ปี พ.ศ. เริ่มต้น -->
            <v-col cols="6" md="2">
              <v-select
                v-model="filters.year_from"
                :items="yearOptions"
                label="ปี (พ.ศ.) ตั้งแต่"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-calendar-start"
                @change="onFilterChange"
              ></v-select>
            </v-col>

            <!-- ปี พ.ศ. สิ้นสุด -->
            <v-col cols="6" md="2">
              <v-select
                v-model="filters.year_to"
                :items="yearOptions"
                label="ปี (พ.ศ.) ถึง"
                outlined dense hide-details clearable
                prepend-inner-icon="mdi-calendar-end"
                @change="onFilterChange"
              ></v-select>
            </v-col>

            <!-- ปุ่มค้นหา -->
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn color="primary" depressed rounded block @click="loadList" :loading="loading">
                <v-icon left small>mdi-magnify</v-icon> ค้นหา
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ── CHART ROW ───────────────────────────────────────────────── -->
      <v-row class="mb-5">
        <!-- แผนภูมิ ประเภทการพ้นสภาพ -->
        <v-col cols="12" md="5">
          <v-card class="rounded-xl" elevation="1" height="100%">
            <v-card-title class="text-body-2 font-weight-bold grey--text py-3 px-4">
              <v-icon left x-small>mdi-chart-pie</v-icon> สัดส่วนประเภทการพ้นสภาพ
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-3">
              <div v-if="summary.reason_breakdown && summary.reason_breakdown.length">
                <div
                  v-for="(r, i) in summary.reason_breakdown"
                  :key="i"
                  class="mb-2"
                >
                  <div class="d-flex justify-space-between text-body-2 mb-1">
                    <span class="text-truncate" style="max-width:200px">{{ r.reason }}</span>
                    <strong>{{ r.cnt }} ราย</strong>
                  </div>
                  <v-progress-linear
                    :value="(r.cnt / summary.summary.total) * 100"
                    :color="reasonBarColor(i)"
                    height="10" rounded
                    background-color="grey lighten-3"
                  ></v-progress-linear>
                </div>
              </div>
              <div v-else class="text-center grey--text py-6">ไม่มีข้อมูล</div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- แผนภูมิ รายปี -->
        <v-col cols="12" md="4">
          <v-card class="rounded-xl" elevation="1" height="100%">
            <v-card-title class="text-body-2 font-weight-bold grey--text py-3 px-4">
              <v-icon left x-small>mdi-chart-bar</v-icon> จำนวนพ้นสภาพรายปี
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-3">
              <div v-if="summary.year_breakdown && summary.year_breakdown.length">
                <div
                  v-for="y in [...summary.year_breakdown].reverse()"
                  :key="y.year_th"
                  class="mb-2"
                >
                  <div class="d-flex justify-space-between text-body-2 mb-1">
                    <span>พ.ศ. {{ y.year_th }}</span>
                    <strong>{{ y.cnt }} ราย</strong>
                  </div>
                  <v-progress-linear
                    :value="(y.cnt / maxYearCount) * 100"
                    color="blue darken-1" height="10" rounded
                    background-color="blue lighten-5"
                  ></v-progress-linear>
                </div>
              </div>
              <div v-else class="text-center grey--text py-6">ไม่มีข้อมูล</div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- TOP วิทยาลัย -->
        <v-col cols="12" md="3">
          <v-card class="rounded-xl" elevation="1" height="100%">
            <v-card-title class="text-body-2 font-weight-bold grey--text py-3 px-4">
              <v-icon left x-small>mdi-school</v-icon> Top วิทยาลัย
            </v-card-title>
            <v-divider></v-divider>
            <v-list dense class="py-0">
              <v-list-item
                v-for="(c, i) in (summary.college_breakdown || []).slice(0, 8)"
                :key="c.college_code"
                dense class="px-4"
              >
                <v-list-item-avatar size="24" class="mr-2 my-1">
                  <span class="text-caption font-weight-bold" :class="i < 3 ? 'error--text' : 'grey--text'">
                    {{ i + 1 }}
                  </span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-caption text-truncate">
                    {{ c.college_name || c.college_code }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <v-chip x-small :color="i < 3 ? 'red lighten-5' : 'grey lighten-3'"
                    :text-color="i < 3 ? 'error' : 'grey darken-1'">
                    {{ c.cnt }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── DATA TABLE ──────────────────────────────────────────────── -->
      <v-card class="rounded-xl" elevation="1">
        <v-toolbar flat color="white" class="px-4 rounded-t-xl" height="56">
          <v-icon small color="grey" class="mr-2">mdi-table</v-icon>
          <span class="font-weight-bold grey--text text--darken-2">รายการพนักงานพ้นสภาพ</span>
          <v-spacer></v-spacer>
          <span class="grey--text text-body-2 mr-3">
            แสดง {{ filteredData.length.toLocaleString() }} รายการ
          </span>
          <!-- Column toggle -->
          <v-menu offset-y left :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" class="mr-1">
                <v-icon small>mdi-view-column</v-icon>
              </v-btn>
            </template>
            <v-card width="260" class="pa-3">
              <div class="text-caption grey--text mb-2">เลือกคอลัมน์ที่แสดง</div>
              <v-checkbox
                v-for="col in toggleableHeaders"
                :key="col.value"
                v-model="visibleCols"
                :value="col.value"
                :label="col.text"
                dense hide-details class="mt-0 mb-1"
              ></v-checkbox>
            </v-card>
          </v-menu>
        </v-toolbar>

        <v-data-table
          :headers="activeHeaders"
          :items="filteredData"
          :items-per-page="15"
          :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }"
          :loading="loading"
          class="admin-table"
          fixed-header
          height="520"
          loading-text="กำลังโหลด..."
          no-data-text="ไม่พบข้อมูล"
        >
          <!-- ชื่อ -->
          <template v-slot:item.frist_name="{ item }">
            <div class="d-flex align-center py-1">
              <v-avatar color="red lighten-5" size="32" class="mr-2">
                <span class="error--text text-caption font-weight-bold">
                  {{ item.frist_name ? item.frist_name.charAt(0) : '?' }}
                </span>
              </v-avatar>
              <div>
                <div class="text-body-2 font-weight-bold" style="white-space:nowrap">
                  {{ item.title_s }}{{ item.frist_name }} {{ item.last_name }}
                </div>
                <div class="grey--text" style="font-size:11px">{{ item.id_card || '-' }}</div>
              </div>
            </div>
          </template>

          <!-- วิทยาลัย -->
          <template v-slot:item.college_name="{ item }">
            <div style="max-width:180px">
              <div class="text-body-2 text-truncate">{{ item.college_name || item.college_code }}</div>
              <div class="grey--text" style="font-size:11px">{{ item.region_name_th || '' }}</div>
            </div>
          </template>

          <!-- ตำแหน่ง -->
          <template v-slot:item.name_postion="{ item }">
            <div style="max-width:160px">
              <div class="text-body-2 text-truncate">{{ item.name_postion }}</div>
              <div class="grey--text" style="font-size:11px">{{ item.id_position }}</div>
            </div>
          </template>

          <!-- ประเภทพ้นสภาพ -->
          <template v-slot:item.reason_type_extract="{ item }">
            <v-chip
              x-small label
              :color="getReasonChip(item.reason_type_extract).bg"
              :text-color="getReasonChip(item.reason_type_extract).text"
              class="font-weight-bold"
            >
              {{ item.reason_type_extract }}
            </v-chip>
          </template>

          <!-- วันลาออก -->
          <template v-slot:item.resignation_date="{ item }">
            <div style="white-space:nowrap">
              <div class="text-body-2 error--text font-weight-medium">
                {{ formatThaiDate(item.resignation_date) }}
              </div>
              <div class="grey--text" style="font-size:11px" v-if="item.service_years !== null">
                อายุงาน {{ item.service_years }} ปี
              </div>
            </div>
          </template>

          <!-- เงินเดือนล่าสุด -->
          <template v-slot:item.salary_s="{ item }">
            <span class="success--text font-weight-bold text-body-2">
              {{ formatCurrency(item.salary_s) }}
            </span>
          </template>

          <!-- จำนวนสัญญา -->
          <template v-slot:item.contract_count="{ item }">
            <v-chip x-small color="blue lighten-5" text-color="blue darken-2">
              {{ item.contract_count || 0 }} ครั้ง
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon x-small color="primary" @click="viewDetail(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- ── DETAIL DIALOG ──────────────────────────────────────────── -->
    <v-dialog v-model="showDetail" max-width="680" scrollable>
      <v-card class="rounded-xl" v-if="selected">
        <div class="detail-header pa-5 d-flex align-center white--text">
          <v-avatar color="white" size="60" class="mr-3 elevation-2">
            <span class="error--text font-weight-black text-h6">
              {{ selected.frist_name ? selected.frist_name.charAt(0) : '?' }}
            </span>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">
              {{ selected.title_s }}{{ selected.frist_name }} {{ selected.last_name }}
            </div>
            <div class="text-body-2 opacity-90">{{ selected.name_postion }}</div>
            <div class="text-caption opacity-80">{{ selected.college_name }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showDetail = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="pa-5" style="max-height:65vh;overflow-y:auto">
          <!-- การลาออก -->
          <div class="section-label mb-2"><v-icon x-small color="error" class="mr-1">mdi-calendar-remove</v-icon> ข้อมูลการลาออก</div>
          <v-row dense class="mb-4">
            <v-col cols="6"><div class="dlabel">ประเภท</div>
              <v-chip x-small label :color="getReasonChip(selected.reason_type_extract).bg" :text-color="getReasonChip(selected.reason_type_extract).text" class="font-weight-bold">{{ selected.reason_type_extract }}</v-chip>
            </v-col>
            <v-col cols="6"><div class="dlabel">วันที่มีผลลาออก</div><div class="dval error--text font-weight-bold">{{ formatThaiDate(selected.resignation_date) }}</div></v-col>
            <v-col cols="6"><div class="dlabel">วันที่บันทึก</div><div class="dval">{{ formatThaiDateTime(selected.resignation_recorded_at) }}</div></v-col>
            <v-col cols="6"><div class="dlabel">อายุงาน</div><div class="dval">{{ selected.service_years !== null ? selected.service_years + ' ปี' : '-' }}</div></v-col>
            <v-col cols="6"><div class="dlabel">จำนวนครั้งสัญญา</div><div class="dval">{{ selected.contract_count || '0' }} ครั้ง</div></v-col>
            <v-col cols="6"><div class="dlabel">เครื่องราชฯ</div><div class="dval">{{ selected.last_royal_decoration || '-' }}</div></v-col>
            <v-col cols="12"><div class="dlabel">รายละเอียด</div>
              <div class="dval pa-2 grey lighten-5 rounded mt-1 text-body-2">{{ extractRemark(selected.remark) || '-' }}</div>
            </v-col>
          </v-row>
          <v-divider class="mb-3"></v-divider>
          <!-- ข้อมูลส่วนตัว -->
          <div class="section-label mb-2"><v-icon x-small color="primary" class="mr-1">mdi-account</v-icon> ข้อมูลส่วนตัว</div>
          <v-row dense class="mb-4">
            <v-col cols="6"><div class="dlabel">เลขบัตรประชาชน</div><div class="dval font-mono">{{ selected.id_card }}</div></v-col>
            <v-col cols="6"><div class="dlabel">เพศ</div><div class="dval">{{ selected.sex || '-' }}</div></v-col>
            <v-col cols="6"><div class="dlabel">วันเกิด</div><div class="dval">{{ formatDateParts(selected.brith_year, selected.brith_month, selected.brith_day) }}</div></v-col>
            <v-col cols="6"><div class="dlabel">ภูมิลำเนา</div><div class="dval">{{ selected.province_name_th || selected.province || '-' }}</div></v-col>
            <v-col cols="6"><div class="dlabel">วันแรกบรรจุ</div><div class="dval">{{ formatDateParts(selected.appoin_year, selected.appoin_month, selected.appoin_day) }}</div></v-col>
            <v-col cols="6"><div class="dlabel">ประเภทตำแหน่ง</div><div class="dval">{{ selected.type_position || '-' }}</div></v-col>
          </v-row>
          <v-divider class="mb-3"></v-divider>
          <!-- เงินเดือน -->
          <div class="section-label mb-2"><v-icon x-small color="success" class="mr-1">mdi-cash</v-icon> เงินเดือน & การศึกษา</div>
          <v-row dense class="mb-4">
            <v-col cols="6"><div class="dlabel">เงินเดือนแรกบรรจุ</div><div class="dval">{{ formatCurrency(selected.salary_start) }}</div></v-col>
            <v-col cols="6"><div class="dlabel">เงินเดือนล่าสุด</div><div class="dval success--text font-weight-bold">{{ formatCurrency(selected.salary_s) }}</div></v-col>
            <v-col cols="6"><div class="dlabel">วุฒิการศึกษา</div><div class="dval">{{ selected.ed_abb }} {{ selected.ed_name }}</div></v-col>
            <v-col cols="6"><div class="dlabel">สถาบัน</div><div class="dval">{{ selected.edd_university || '-' }}</div></v-col>
          </v-row>
          <!-- วินัย -->
          <template v-if="selected.disciplinary_offense || selected.disciplinary_history">
            <v-divider class="mb-3"></v-divider>
            <div class="section-label mb-2"><v-icon x-small color="deep-purple" class="mr-1">mdi-gavel</v-icon> ประวัติวินัย</div>
            <v-row dense>
              <v-col cols="12" v-if="selected.disciplinary_offense">
                <div class="dlabel">ความผิดทางวินัย</div>
                <div class="dval pa-2 red lighten-5 rounded mt-1 error--text text-body-2">{{ selected.disciplinary_offense }}</div>
              </v-col>
              <v-col cols="12" v-if="selected.disciplinary_history">
                <div class="dlabel">ประวัติทางวินัย</div>
                <div class="dval pa-2 orange lighten-5 rounded mt-1 text-body-2">{{ selected.disciplinary_history }}</div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right rounded="pill" timeout="3000">
      <v-icon small class="mr-2">{{ snackbar.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
      {{ snackbar.message }}
    </v-snackbar>

  </div>
</template>

<script>
export default {
  name: 'AdminResignReport',

  data() {
    return {
      ApiKey: 'HRvec2021',
      loading: false,
      exporting: false,
      rawData: [],
      filteredData: [],
      collegeOptions: [],
      regionOptions: [],
      summary: {},
      totalCount: 0,
      showDetail: false,
      selected: null,
      snackbar: { show: false, message: '', color: 'success' },

      filters: {
        search: '',
        college_code: null,
        region_name: null,
        reason_type: null,
        type_position: null,
        year_from: null,
        year_to: null
      },

      reasonTypeOptions: [
        'ลาออกด้วยความสมัครใจ',
        'ครบกำหนดสัญญาจ้าง',
        'เกษียณอายุ',
        'ถูกเลิกจ้าง',
        'โอนย้าย',
        'ถูกไล่ออก / ปลดออก (วินัย)',
        'เสียชีวิต',
        'อื่น ๆ'
      ],

      reasonColors: [
        { bg: 'orange lighten-5', text: 'orange darken-3' },
        { bg: 'blue lighten-5',   text: 'blue darken-3' },
        { bg: 'teal lighten-5',   text: 'teal darken-3' },
        { bg: 'red lighten-5',    text: 'red darken-3' },
        { bg: 'purple lighten-5', text: 'purple darken-3' },
        { bg: 'deep-orange lighten-5', text: 'deep-orange darken-3' },
        { bg: 'grey lighten-3',   text: 'grey darken-3' },
        { bg: 'grey lighten-3',   text: 'grey darken-2' }
      ],

      barColors: ['error','orange','blue','teal','purple','deep-orange','grey','brown'],

      visibleCols: [
        'frist_name','college_name','name_postion',
        'reason_type_extract','resignation_date',
        'salary_s','contract_count','actions'
      ],

      toggleableHeaders: [
        { text: 'ชื่อ-นามสกุล',         value: 'frist_name' },
        { text: 'วิทยาลัย',             value: 'college_name' },
        { text: 'ตำแหน่ง',             value: 'name_postion' },
        { text: 'ประเภทพ้นสภาพ',       value: 'reason_type_extract' },
        { text: 'วันที่ลาออก',         value: 'resignation_date' },
        { text: 'เพศ',                  value: 'sex' },
        { text: 'วุฒิ',                value: 'ed_abb' },
        { text: 'เงินเดือนล่าสุด',     value: 'salary_s' },
        { text: 'จำนวนครั้งสัญญา',    value: 'contract_count' },
        { text: 'อายุงาน (ปี)',         value: 'service_years' },
        { text: 'ดูรายละเอียด',        value: 'actions' }
      ]
    }
  },

  computed: {
    activeHeaders() {
      return this.toggleableHeaders.filter(h => this.visibleCols.includes(h.value))
    },

    summaryCards() {
      const s = this.summary.summary || {}
      return [
        { label: 'พ้นสภาพทั้งหมด',      value: (+s.total || 0).toLocaleString(),   icon: 'mdi-account-off',        iconColor: 'error',   textColor: 'error--text',   bg: 'red lighten-5' },
        { label: 'วิทยาลัยที่เกี่ยวข้อง', value: (+s.college_count || 0),           icon: 'mdi-domain',             iconColor: 'primary', textColor: 'primary--text', bg: 'blue lighten-5' },
        { label: 'เพศชาย',              value: (+s.male_count || 0),                icon: 'mdi-gender-male',        iconColor: 'blue darken-2', textColor: 'blue--text text--darken-2', bg: 'blue lighten-5' },
        { label: 'เพศหญิง',             value: (+s.female_count || 0),              icon: 'mdi-gender-female',      iconColor: 'pink',    textColor: 'pink--text',    bg: 'pink lighten-5' },
        { label: 'อายุงานเฉลี่ย (ปี)',  value: s.avg_service_years ? (+s.avg_service_years).toFixed(1) : '-', icon: 'mdi-briefcase-clock', iconColor: 'orange darken-2', textColor: 'orange--text text--darken-2', bg: 'orange lighten-5' },
        { label: 'พ้นสภาพปีนี้',        value: (+s.this_year_count || 0),           icon: 'mdi-calendar-today',     iconColor: 'success', textColor: 'success--text', bg: 'green lighten-5' }
      ]
    },

    yearOptions() {
      const cur = new Date().getFullYear() + 543
      const opts = []
      for (let y = cur; y >= cur - 20; y--) opts.push(y)
      return opts
    },

    maxYearCount() {
      if (!this.summary.year_breakdown.length) return 1
      return Math.max(...this.summary.year_breakdown.map(y => +y.cnt))
    }
  },

  async mounted() {
    await Promise.all([this.loadSummary(), this.loadList()])
  },

  methods: {
    async loadSummary() {
      try {
        const res = await this.$http.post('government_employee_del_admin.php', {
          ApiKey: this.ApiKey,
          request_type: 'summary',
          ...this.filters
        })
        this.summary = res.data
      } catch { /* silent */ }
    },

    async loadList() {
      this.loading = true
      try {
        const res = await this.$http.post('government_employee_del_admin.php', {
          ApiKey: this.ApiKey,
          request_type: 'list',
          ...this.filters
        })
        this.rawData      = Array.isArray(res.data.data) ? res.data.data : []
        this.filteredData = [...this.rawData]
        this.totalCount   = res.data.total || 0
        if (res.data.colleges) this.collegeOptions = res.data.colleges
        if (res.data.regions)  this.regionOptions  = res.data.regions
      } catch {
        this.showSnackbar('โหลดข้อมูลไม่สำเร็จ', 'error')
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      // client-side filter ทันที สำหรับ search
      if (!this.filters.search) {
        this.filteredData = [...this.rawData]
        return
      }
      const q = this.filters.search.toLowerCase()
      this.filteredData = this.rawData.filter(e =>
        [e.frist_name, e.last_name, e.id_card, e.name_postion, e.college_name]
          .some(v => v && String(v).toLowerCase().includes(q))
      )
    },

    clearFilters() {
      this.filters = { search:'', college_code:null, region_name:null, reason_type:null, type_position:null, year_from:null, year_to:null }
      this.loadList()
      this.loadSummary()
    },

    viewDetail(item) { this.selected = item; this.showDetail = true },

    async exportExcel() {
      this.exporting = true
      try {
        const res = await this.$http.post('government_employee_del_export.php', {
          ApiKey: this.ApiKey,
          ...this.filters
        }, { responseType: 'blob' })

        const url  = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href  = url
        const now  = new Date()
        const yy   = now.getFullYear() + 543
        const mm   = String(now.getMonth()+1).padStart(2,'0')
        const dd   = String(now.getDate()).padStart(2,'0')
        link.setAttribute('download', `รายงานพนักงานพ้นสภาพ_${yy}${mm}${dd}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        this.showSnackbar('Export Excel สำเร็จ', 'success')
      } catch {
        this.showSnackbar('Export ไม่สำเร็จ กรุณาลองใหม่', 'error')
      } finally {
        this.exporting = false
      }
    },

    // ── Helpers ──
    reasonBarColor(i) { return this.barColors[i % this.barColors.length] },

    getReasonChip(type) {
      const map = {
        'ลาออกด้วยความสมัครใจ':       { bg:'orange lighten-5', text:'orange darken-3' },
        'ครบกำหนดสัญญาจ้าง':          { bg:'blue lighten-5',   text:'blue darken-3' },
        'เกษียณอายุ':                  { bg:'teal lighten-5',   text:'teal darken-3' },
        'ถูกเลิกจ้าง':                 { bg:'red lighten-5',    text:'red darken-3' },
        'โอนย้าย':                     { bg:'purple lighten-5', text:'purple darken-3' },
        'ถูกไล่ออก / ปลดออก (วินัย)': { bg:'deep-orange lighten-5', text:'deep-orange darken-3' },
        'เสียชีวิต':                   { bg:'grey lighten-3',   text:'grey darken-3' },
      }
      return map[type] || { bg:'grey lighten-3', text:'grey darken-2' }
    },

    extractRemark(remark) {
      if (!remark) return ''
      return remark.replace(/^\[.+?\]\s*/, '').replace(/\s*\[ไฟล์แนบ:.*?\]$/, '').trim()
    },

    formatThaiDate(d) {
      if (!d) return '-'
      const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
      const dt = new Date(d)
      if (isNaN(dt)) return d
      return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()+543}`
    },

    formatThaiDateTime(d) {
      if (!d) return '-'
      const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
      const dt = new Date(d)
      if (isNaN(dt)) return d
      return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()+543} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')} น.`
    },

    formatDateParts(y, m, d) {
      if (!y || !m || !d) return '-'
      const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
      const mi = parseInt(m)-1
      return `${parseInt(d)} ${months[mi] || ''} ${parseInt(y)}`
    },

    formatCurrency(v) {
      if (!v || v == 0) return '-'
      return '฿' + parseFloat(v).toLocaleString('th-TH', { minimumFractionDigits: 2 })
    },

    showSnackbar(message, color='success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>

<style scoped>
.admin-resign-container {
  background: #f4f7fc;
  min-height: 100vh;
  font-family: 'Sarabun', sans-serif;
}

.detail-header {
  background: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
  min-height: 100px;
}

.admin-table >>> th {
  font-size: 0.82rem !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6b7280 !important;
  background: #fff !important;
  white-space: nowrap;
}
.admin-table >>> td {
  font-size: 0.9rem !important;
  color: #374151;
  border-bottom: 1px solid #f3f4f6 !important;
}
.admin-table >>> tbody tr:hover { background: #fef2f2 !important; }

.section-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #374151;
  display: flex;
  align-items: center;
}
.dlabel { font-size: 0.75rem; color: #9ca3af; margin-bottom: 2px; }
.dval   { font-size: 0.9rem;  color: #1f2937; font-weight: 500; }
.font-mono { font-family: 'Courier New', monospace; }
.opacity-80 { opacity: 0.8; }
.opacity-90 { opacity: 0.9; }
</style>